import { createSlice } from '@reduxjs/toolkit';

import { insuranceApi } from '@/integrations/app.api/insurance.api';
import { handleError } from '@/services/handleError';
import { insuranceItemMapper } from '@/services/mappers/insuranceMapperItem';
import { insuranceTableMapper } from '@/services/mappers/insuranceMapperResp';
import type { GetFilteredInsurancesFilters } from '@/types/insuranseServer';
import type { TNewInsuranceDto } from '@/types/newInsurance';
import type { TOrderInsuranceStore } from '@/types/store';

import { createAsyncThunk } from './create-async-thunk';

const initialState: TOrderInsuranceStore = {
  description: {
    data: ''
  },
  isLoading: false,
  otpCode: '',
  uuid: '',

  autoInsuranceInfo: {
    dateOfTechPasspord: '',
    dealer: '',
    markModelYear: {
      year: ''
    },
    numberGos: '',
    priseAvto: '',
    tehNum: '',
    vinCode: ''
  },
  clientInsuranceInfo: {
    innInsurance: '',
    numberInsurance: ''
  },
  insurance: {
    insuransePremium: '',
    insuranseSumm: ''
  },
  insurances: {
    data: [],
    total: 0,
    pageCount: 0
  },
  personalInsuranceInfo: {
    address: '',
    clientIin: '',
    clientPhone: '',
    dateBirth: '',
    dateDocument: '',
    docType: '',
    email: '',
    firstName: '',
    issuebdy: '',
    lastName: '',
    middleName: '',
    numberdocument: '',
    resident: '',
    sex: '',
    validity: ''
  },
  productInsuranceInfo: {
    dateEndpolis: '',
    dateForm: '',
    datepolis: '',
    payment: '',
    paymentType: '',
    productType: '',
    productTypeDate: '',
    status: {
      code: '',
      name: '',
      uuid: ''
    },
    isKdpActive: false
  }
};

export const createInsuranceOrder = createAsyncThunk(
  'ínsurance/create',
  async (data: TNewInsuranceDto) => {
    return await insuranceApi.createInsuranceOrder(data);
  }
);

export const requestCreateOtp = createAsyncThunk(
  'insurances/createOTP',
  async (data: {
    clientPhone: string;
    iin: string;
    last_name: string;
    first_name: string;
    middle_name: string;
  }) => {
    return await insuranceApi.requestCreateOtpInsurance(data);
  }
);

export const validateInsuranceOTP = createAsyncThunk(
  'insurances/validateOTP',
  async (data: { otp: string; uuid: string }) => {
    return await insuranceApi.validateOTPInsurance(data);
  }
);

export const getOneInsuranceReq = createAsyncThunk(
  'insurances/findByInsuranceId',
  async (id: string) => {
    return await insuranceApi.getOneInsurance(id);
  }
);

export const getFilteredInsurances = createAsyncThunk(
  'insurances/getFilteredInsurances',
  async (filters: GetFilteredInsurancesFilters) => {
    return await insuranceApi.getFilteredInsurances(filters);
  }
);

export const orderInsuranceSlice = createSlice({
  name: 'orderInsuranceSlice',
  initialState,
  reducers: {
    onSetInsurancesStoreToInitial() {
      return initialState;
    },
    onSetClientInsuranceInfo(state, action) {
      state.clientInsuranceInfo = action.payload;
    },
    resetInsuranceStore() {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createInsuranceOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        const parsedData = insuranceItemMapper(action.payload);
        state.autoInsuranceInfo = parsedData.autoInsuranceInfo;
        state.clientInsuranceInfo = parsedData.clientInsuranceInfo;
        state.insurance = parsedData.insurance;
        state.personalInsuranceInfo = parsedData.personalInsuranceInfo;
        state.productInsuranceInfo = parsedData.productInsuranceInfo;
      })
      .addCase(createInsuranceOrder.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createInsuranceOrder.rejected, (state) => {
        state.isLoading = false;
      })

      .addCase(requestCreateOtp.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(requestCreateOtp.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(requestCreateOtp.rejected, (state) => {
        state.isLoading = false;
      })

      .addCase(validateInsuranceOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.otpCode = action.payload.otpCode;
      })
      .addCase(validateInsuranceOTP.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(validateInsuranceOTP.rejected, (state) => {
        state.isLoading = false;
      })

      .addCase(getFilteredInsurances.fulfilled, (state, action) => {
        state.isLoading = false;
        state.insurances = insuranceTableMapper(action.payload);
      })
      .addCase(getFilteredInsurances.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFilteredInsurances.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getOneInsuranceReq.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.description) {
          try {
            const description = JSON.parse(action.payload.description);
            const descriptionData = description.data;
            if (descriptionData && typeof descriptionData === 'string') {
              state.description.data = descriptionData;
            } else {
              state.description.data = '';
            }
          } catch (error) {
            handleError(error, {
              notify: false,
              customTitle: 'Ошибка парсинга description',
              onError: () => {
                state.description.data = '';
              }
            });
          }
        }
        const parsedData = insuranceItemMapper(action.payload);
        state.autoInsuranceInfo = parsedData.autoInsuranceInfo;
        state.clientInsuranceInfo = parsedData.clientInsuranceInfo;
        state.insurance = parsedData.insurance;
        state.personalInsuranceInfo = parsedData.personalInsuranceInfo;
        state.productInsuranceInfo = parsedData.productInsuranceInfo;
      })
      .addCase(getOneInsuranceReq.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOneInsuranceReq.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

export const {
  onSetClientInsuranceInfo,
  onSetInsurancesStoreToInitial,
  resetInsuranceStore
} = orderInsuranceSlice.actions;
